/**
 * Phase1MachineGrid.tsx
 * FEAT-2026-0007 Phase 6B.5 — Phase-1 machine daily grid below the task table.
 *
 * Rows = union of all capable machines for the current scenario tasks.
 * Bars  = scenario-overlaid: committed-baseline bars + this scenario's
 *         tentative (proposed) bars (fetched with scenarioKey=...).
 *
 * 6B.8 — Compare-to-Baseline ghost overlay:
 *   When compareToBaseline=true, a second fetch (no scenarioKey) retrieves the
 *   committed+BASELINE-tentative view. Bars that already appear in the scenario
 *   layer (same MachineBookingKey) are filtered out to avoid double-rendering
 *   identical committed bars. The remaining baseline bars are passed to
 *   MachineScheduleGrid as `ghostBookings` and rendered as outline-only ghost
 *   bars behind the scenario layer (zIndex:0, reduced opacity).
 *
 * Live-updates when `refreshKey` increments (parent bumps it on every
 * task-table mutation so the grid refetches bookings automatically).
 *
 * Composed from existing components — no forked logic:
 *   MachineScheduleGrid  — row + bar renderer
 *   MachineScheduleLegend — color key
 *   MachineScheduleHeader — sticky day-header (owned by MachineScheduleGrid internally)
 *   BookingBar            — used by MachineScheduleGrid (not imported here directly)
 *
 * Drag-to-reschedule (BUG-2026-0100): the booking bars rendered by
 * MachineScheduleGrid are always draggable (move / resize-start / resize-end /
 * cross-machine move). Previously this grid passed a no-op `onCommitPlacement`,
 * so a drag looked interactive but the date never changed. We now persist the
 * drag via `updateBookingPlacement` (PUT /schedule/bookings/:key), which updates
 * only StartDate/EndDate/MachineKey and preserves the booking's ScenarioKey —
 * so the scenario's tentative bar stays in-scenario. After a successful commit we
 * refetch bookings + conflicts so both the bar position and the conflicts panel
 * reflect the new dates. The endpoint is not conflict-blocking (soft-conflict
 * model, matching the standalone Machine Schedule view); new conflicts surface in
 * the SchedulingConflictsPanel (6B.6). Create-drag and the booking-detail drawer
 * remain intentionally disabled inside the planning wizard.
 *
 * Auto-update the top task table (BUG-2026-0105): the drag commit above refreshed
 * only this grid's own bookings + conflicts, so the parent's "Project Tasks" table
 * kept showing the pre-drag machine + dates. We now fire `onPlacementCommitted`
 * after a successful drag so Phase1Panel reloads its task list — the symmetric
 * counterpart to the parent's `bumpGrid()`, which already refreshes this grid when
 * the table is edited. The table rows derive Start/End date + Assigned Machine(s)
 * from each booking, so the reload reflects exactly what the drag moved.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { MachineScheduleGrid } from '../MachineScheduleGrid';
import { MachineScheduleLegend } from '../MachineScheduleLegend';
import {
  fetchMachineBookings,
  fetchScheduleConflicts,
} from '../../../../services/scheduleBookings';
import type { MachineBookingCalendar, ConflictSummary } from '../../../../types/schedule';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';
import type { BookingPlacementPatch } from '../../../../hooks/useScheduleData';
import { usePhase1CapableMachines } from './usePhase1CapableMachines';
import { MachineSectionFilter } from './MachineSectionFilter';
import { useMachineSectionFilter } from './useMachineSectionFilter';
import { useDragPlacementConfirm } from './useDragPlacementConfirm';
import { PlacementConfirmDialog } from '../../../../components/schedule/PlacementConfirmDialog';
import { ScheduleGridDisplayControl } from '../../../../components/schedule/ScheduleGridDisplayControl';
import { useScheduleGridDisplay } from '../../../../hooks/useScheduleGridDisplay';
import { APP_BAR_HEIGHT } from '../../../../components/layout/AppShell';
import { COMPACT_ROW_HEIGHT, ROW_HEIGHT } from '../scheduleGridShared';
import { usePhase1Window } from './usePhase1Window';
import { Phase1WindowControl } from './Phase1WindowControl';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Phase1MachineGridProps {
  /** Current task list — drives the date window and the capable-machine union. */
  tasks: TaskWithBookings[];
  /** Active scenario key — overlays this draft's tentative bars on the baseline. */
  scenarioKey: string | null;
  /** Increment this counter to trigger a bookings refetch (after a task mutation). */
  refreshKey: number;
  /**
   * When true (6B.8), fetches the committed+BASELINE layer and renders it as
   * ghost bars behind the scenario's proposed bars in MachineScheduleGrid.
   */
  compareToBaseline: boolean;
  /**
   * Called whenever conflicts are refreshed so the parent can render the
   * SchedulingConflictsPanel with up-to-date counts + details (6B.6).
   */
  onConflictsChange?: (conflicts: ConflictSummary) => void;
  /**
   * Ref forwarded to the scrollable grid container so the parent can
   * query `[data-booking-key="..."]` for jump-to-conflict behaviour (6B.6).
   */
  gridContainerRef?: React.RefObject<HTMLDivElement>;
  /**
   * BUG-2026-0105 — fired after a drag (move / resize / cross-machine) is
   * persisted successfully, so the parent can reload the top task table. The
   * grid already refetches its OWN bookings + conflicts via bumpLocal(); this
   * is the symmetric callback for the reverse direction (the parent owns the
   * task list, whose rows derive Start/End date and Assigned Machine(s) from
   * the booking the drag just moved). Without it the bottom Gantt updates but
   * the top "Project Tasks" table stays stale.
   */
  onPlacementCommitted?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase1MachineGrid: React.FC<Phase1MachineGridProps> = ({
  tasks,
  scenarioKey,
  refreshKey,
  compareToBaseline,
  onConflictsChange,
  gridContainerRef,
  onPlacementCommitted,
}) => {
  const theme = useTheme();

  // ── Date window: tasks' span ±3d, extendable in either direction ──────────
  // BUG-2026-0178 — the window used to be pinned to the task span, so the
  // planner ran out of calendar at the grid's right edge. usePhase1Window adds
  // an open-ended extension: the header's ◀◀/▶▶ buttons and the grid's own
  // ◀/▶ paging arrows (when already at an edge) each grow the window 4 weeks.
  const {
    start: windowStart,
    end: windowEnd,
    extend: extendWindow,
    resetToFit: resetWindow,
    isExtended: windowExtended,
  } = usePhase1Window(tasks);

  // ── Capable-machine union (rows) + taskType → machines (drop constraint) ──
  // Extracted to usePhase1CapableMachines (600-line cap); behaviour unchanged.
  const { capableMachines, capabilityByTaskType } = usePhase1CapableMachines(tasks);

  // ── Bookings fetch (scenario-overlaid) ───────────────────────────────────
  const [bookings, setBookings] = useState<MachineBookingCalendar[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Local refetch trigger — bumped after a successful in-grid drag commit so the
  // bookings + conflicts effects re-run (the parent's `refreshKey` covers
  // task-table mutations; this covers drag-to-reschedule on the grid itself).
  // BUG-2026-0100.
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const bumpLocal = useCallback(() => setLocalRefreshKey((k) => k + 1), []);

  // Transient info message (e.g. an invalid cross-machine drop the drag engine
  // rejected). Shown in a Snackbar so the planner gets feedback on a no-op drop.
  const [info, setInfo] = useState<string | null>(null);

  const loadBookings = useCallback(async () => {
    if (tasks.length === 0) {
      setBookings([]);
      return;
    }
    setLoadingBookings(true);
    setBookingError(null);
    try {
      const data = await fetchMachineBookings({
        startDate: windowStart,
        endDate: windowEnd,
        scenarioKey: scenarioKey ?? undefined,
        // "Show the blocker": also pull other drafts' tentative bookings so bars
        // reserving a slot that looks empty here are visible (isOtherScenario).
        includeOtherScenarios: true,
      });
      setBookings(data);
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : 'Failed to load machine schedule.');
    } finally {
      setLoadingBookings(false);
    }
  }, [tasks.length, windowStart, windowEnd, scenarioKey]);

  // Refetch whenever the window, scenario, the parent's refreshKey, or a local
  // drag commit (localRefreshKey) changes.
  useEffect(() => {
    void loadBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, windowEnd, scenarioKey, refreshKey, localRefreshKey]);

  // ── Baseline fetch (6B.8) — only when compareToBaseline is ON ─────────────
  /**
   * The "baseline" = fetchMachineBookings with NO scenarioKey, which returns
   * all Committed rows plus BASELINE-tentative rows (the live schedule without
   * this scenario's proposals).  Bars whose MachineBookingKey already exists in
   * the scenario layer are suppressed to avoid confusingly doubling up identical
   * committed bars.
   */
  const [baselineBookings, setBaselineBookings] = useState<MachineBookingCalendar[]>([]);

  useEffect(() => {
    if (!compareToBaseline || tasks.length === 0) {
      setBaselineBookings([]);
      return;
    }
    fetchMachineBookings({ startDate: windowStart, endDate: windowEnd })
      .then((data) => {
        // Deduplicate: remove baseline bars whose key is already in the scenario layer.
        const scenarioKeys = new Set(bookings.map((b) => b.MachineBookingKey));
        setBaselineBookings(data.filter((b) => !scenarioKeys.has(b.MachineBookingKey)));
      })
      .catch(() => {
        // Best-effort — ghost layer simply stays empty on error.
        setBaselineBookings([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareToBaseline, windowStart, windowEnd, refreshKey, bookings]);

  // ── Lab-section (soil/rock) filter — BUG-2026-0121 #2 ─────────────────────
  // Planners can hide a whole section (e.g. "Soil") to focus on the rock fleet.
  // The hook persists the hidden set and filters BOTH the machine rows and their
  // bookings/ghosts by section (the grid re-creates rows for orphan bookings, so
  // rows alone aren't enough).
  const {
    hiddenSections,
    onHiddenChange: handleHiddenSectionsChange,
    presentSections,
    visibleMachines,
    visibleBookings,
    visibleGhostBookings,
    allHiddenByFilter,
  } = useMachineSectionFilter(capableMachines, bookings, baselineBookings);

  // ── BUG-2026-0132 #2 — capable machines only ──────────────────────────────
  // MachineScheduleGrid auto-creates a row for any booking whose machine isn't
  // in `allMachineRows` (orphan rows). With includeOtherScenarios fetching every
  // machine's bookings in the window, that surfaced machines that CAN'T run the
  // current tests. Drop bookings/ghosts on non-capable machines so only capable
  // machine rows appear; other-project bars on a CAPABLE machine still show (as
  // read-only context, see #1).
  const capableMachineKeys = useMemo(
    () => new Set(visibleMachines.map((m) => m.machineKey)),
    [visibleMachines],
  );
  const gridBookings = useMemo(
    () => visibleBookings.filter((b) => capableMachineKeys.has(b.MachineKey)),
    [visibleBookings, capableMachineKeys],
  );
  const gridGhostBookings = useMemo(
    () => visibleGhostBookings.filter((b) => capableMachineKeys.has(b.MachineKey)),
    [visibleGhostBookings, capableMachineKeys],
  );

  // ── BUG-2026-0132 #1 — read-only foreign bars ─────────────────────────────
  // A bar is THIS scenario's to move only if it belongs to one of the current
  // tasks AND isn't another draft's tentative hold. Everything else — other
  // projects' committed work, another scenario's bars (same shared task, diff
  // ScenarioKey), and manual events (null task) — is locked: visible for
  // context but not draggable/resizable.
  const currentTaskKeys = useMemo(
    () => new Set(tasks.map((t) => t.ScheduleTaskKey)),
    [tasks],
  );
  const isBookingReadOnly = useCallback(
    (b: MachineBookingCalendar): boolean =>
      b.isOtherScenario === true || !b.ScheduleTaskKey || !currentTaskKeys.has(b.ScheduleTaskKey),
    [currentTaskKeys],
  );

  // ── Drag commit (move / resize / cross-machine) — BUG-2026-0100 / drag-parity ──
  // BUG-2026-0105 — ref-stable reference to the parent's reload callback so the
  // drag callbacks stay stable across parent re-renders (the parent passes an
  // inline arrow). Mirrors onConflictsChangeRef below.
  const onPlacementCommittedRef = useRef(onPlacementCommitted);
  onPlacementCommittedRef.current = onPlacementCommitted;

  /**
   * Optimistically reflect a drag patch on the bar so it doesn't visibly snap
   * back while the request is in flight (and while the warn-and-confirm dialog
   * is open). The refetch in onCommitted / revert reconciles to server truth.
   */
  const applyOptimistic = useCallback(
    (booking: MachineBookingCalendar, patch: BookingPlacementPatch) => {
      setBookings((prev) =>
        prev.map((b) =>
          b.MachineBookingKey === booking.MachineBookingKey
            ? {
                ...b,
                StartDate: patch.StartDate ?? b.StartDate,
                EndDate: patch.EndDate ?? b.EndDate,
                MachineKey: patch.MachineKey ?? b.MachineKey,
              }
            : b,
        ),
      );
      setBookingError(null);
    },
    [],
  );

  // After a successful persist: reconcile the grid (covers success + cross-machine
  // moves) and tell the parent to reload the top task table (BUG-2026-0105).
  const handleCommitted = useCallback(() => {
    bumpLocal();
    onPlacementCommittedRef.current?.();
  }, [bumpLocal]);

  const handleDragError = useCallback((msg: string) => setBookingError(msg), []);

  /**
   * Drag parity with the top-table Start editor: every drop runs the backend's
   * warn-and-confirm check (overlap + weekend/holiday). A clean drop persists
   * immediately; a contested one opens PlacementConfirmDialog ("book anyway /
   * use earliest free / cancel"). See useDragPlacementConfirm.
   */
  const drag = useDragPlacementConfirm({
    applyOptimistic,
    revert: bumpLocal,
    onCommitted: handleCommitted,
    onError: handleDragError,
  });

  // Single-click on a bar → no detail drawer inside the planning wizard.
  const noopClick = useCallback(() => {
    // No detail drawer in the planning grid.
  }, []);
  // Invalid cross-machine drop (rejected by the drag engine) → surface why.
  const handleInfo = useCallback((message: string) => {
    setInfo(message);
  }, []);

  // ── Scenario-scoped conflict detection (6B.6) ─────────────────────────────
  const [conflicts, setConflicts] = useState<ConflictSummary>({ hard: [], soft: [] });
  const onConflictsChangeRef = useRef(onConflictsChange);
  // Keep the ref current without adding onConflictsChange to the fetch effect deps.
  onConflictsChangeRef.current = onConflictsChange;

  // Re-fetch conflicts whenever the booking window or refreshKey changes.
  // Runs after loadBookings so the grid and the panel stay in sync.
  useEffect(() => {
    if (tasks.length === 0) {
      const empty: ConflictSummary = { hard: [], soft: [] };
      setConflicts(empty);
      onConflictsChangeRef.current?.(empty);
      return;
    }
    fetchScheduleConflicts({
      startDate: windowStart,
      endDate: windowEnd,
      scenarioKey: scenarioKey ?? undefined,
    })
      .then((data) => {
        setConflicts(data);
        onConflictsChangeRef.current?.(data);
      })
      .catch(() => {
        // Best-effort — conflict panel will show stale/empty data on error.
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, windowEnd, scenarioKey, refreshKey, localRefreshKey, tasks.length]);

  // ── Grid display prefs (sticky-headers UX pass): zoom + compact rows ──────
  const { dayColWidth, zoomDayColWidth, compactRows, setCompactRows } =
    useScheduleGridDisplay();

  // ── Guard: no tasks ────────────────────────────────────────────────────────
  if (tasks.length === 0) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>

      {/* ── Section header ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
        <CalendarMonthIcon sx={{ fontSize: 18, color: theme.schedule.machineTime }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
          Machine Schedule
        </Typography>
        <Box sx={{ flex: 1 }} />

        {/* Zoom (−/+ and Ctrl+wheel) and compact-rows toggle */}
        <ScheduleGridDisplayControl
          dayColWidth={dayColWidth}
          onZoom={zoomDayColWidth}
          compactRows={compactRows}
          onCompactRowsChange={setCompactRows}
        />

        {/* Lab-section (soil/rock) filter — BUG-2026-0121 #2 */}
        <MachineSectionFilter
          sections={presentSections}
          hidden={hiddenSections}
          onChange={handleHiddenSectionsChange}
        />

        {compareToBaseline && (
          <Tooltip title="Ghosted baseline bars (outline-only, reduced opacity) are shown behind the scenario's proposed bars so you can see what shifts from the committed schedule.">
            <Box
              sx={{
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                fontSize: '0.72rem',
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            >
              Baseline overlay ON
              {baselineBookings.length > 0
                ? ` (${baselineBookings.length} ghosted)`
                : ''}
            </Box>
          </Tooltip>
        )}

        {/* Visible range + "show earlier / later weeks" + fit reset (BUG-2026-0178) */}
        <Phase1WindowControl
          windowStart={windowStart}
          windowEnd={windowEnd}
          isExtended={windowExtended}
          onExtend={extendWindow}
          onResetToFit={resetWindow}
          loading={loadingBookings}
        />
      </Box>

      {/* ── Error banner ──────────────────────────────────────────────────── */}
      {bookingError && (
        <Alert severity="error" onClose={() => setBookingError(null)}>
          {bookingError}
        </Alert>
      )}

      {/* ── No capable machines notice ────────────────────────────────────── */}
      {!loadingBookings && capableMachines.length === 0 && tasks.length > 0 && (
        <Alert severity="info">
          No machines are configured as capable of the current task types.
          Set up machine capabilities on the Machines page.
        </Alert>
      )}

      {/* ── All sections hidden by the filter (BUG-2026-0121 #2) ──────────── */}
      {allHiddenByFilter && (
        <Alert severity="info" onClose={() => handleHiddenSectionsChange(new Set())}>
          All machine sections are hidden by the “Show sections” filter. Re-enable a
          section (or clear the filter) to see the machine schedule.
        </Alert>
      )}

      {/* ── Grid ─────────────────────────────────────────────────────────── */}
      {visibleMachines.length > 0 && (
        <Box
          ref={gridContainerRef}
          sx={{
            border: `1px solid ${theme.border.color}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <MachineScheduleGrid
            bookings={gridBookings}
            allMachineRows={visibleMachines}
            startDate={windowStart}
            endDate={windowEnd}
            onCommitPlacement={drag.request}
            onBookingClick={noopClick}
            onInfo={handleInfo}
            capabilityByTaskType={capabilityByTaskType}
            // Was hard-forced expanded, so the lab-section group headers looked
            // clickable but never collapsed (FEAT-2026-0014 feedback). Allow the
            // planner to collapse sections here too — they can re-open to place work.
            forceExpandAll={false}
            showCapacity={false}
            conflicts={conflicts}
            ghostBookings={compareToBaseline ? gridGhostBookings : undefined}
            isBookingReadOnly={isBookingReadOnly}
            // Sticky-headers UX pass (Option 1): day-header pins under the fixed
            // AppBar while the page scrolls, releasing at the grid's end; the
            // machine column is already sticky-left. Zoom + compact rows are
            // user prefs shared with the standalone Machine Schedule page.
            pageFlowStickyTop={APP_BAR_HEIGHT}
            dayColWidth={dayColWidth}
            onDayColWidthDelta={zoomDayColWidth}
            rowHeight={compactRows ? COMPACT_ROW_HEIGHT : ROW_HEIGHT}
            compactLabels={compactRows}
            // BUG-2026-0178: paging ◀/▶ while already at a scroll edge grows the
            // date window instead of dead-ending — "scroll as far as you need".
            onPageBeyondEdge={extendWindow}
          />
        </Box>
      )}

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      {visibleMachines.length > 0 && <MachineScheduleLegend showOtherPlan />}

      {/* ── Drag hint (BUG-2026-0100) ─────────────────────────────────────── */}
      {visibleMachines.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: 'flex-end', fontSize: '0.7rem' }}
        >
          Tip: drag a bar to reschedule it, drag an edge to resize, or drag it onto
          another capable machine row to reassign.
        </Typography>
      )}

      {/* ── Invalid-drop / info snackbar (BUG-2026-0100) ──────────────────── */}
      <Snackbar
        open={info !== null}
        autoHideDuration={4000}
        onClose={() => setInfo(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setInfo(null)} sx={{ width: '100%' }}>
          {info}
        </Alert>
      </Snackbar>

      {/* ── Warn-and-confirm gate for a contested drop (overlap / weekend) ──── */}
      <PlacementConfirmDialog
        open={drag.pending !== null}
        conflict={drag.pending?.conflict ?? false}
        weekend={drag.pending?.weekend ?? false}
        conflictLabel={drag.pending?.conflictLabel ?? null}
        earliestFree={drag.pending?.earliestFree ?? null}
        saving={drag.saving}
        onConfirm={() => { void drag.confirm(); }}
        onUseEarliest={() => { void drag.useEarliest(); }}
        onCancel={drag.cancel}
      />

    </Box>
  );
};
